/**
 * SignUpWithPlanForm - Modal signup form for selected plan
 * Calls auth_signup-with-plan Edge Function
 */

import { useState } from "react";
import { X, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/lib/supabase";

interface SignUpWithPlanFormProps {
  planCode: string;
  billingInterval: "monthly" | "annual";
  onClose: () => void;
}

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  companyName: string;
  agreeToTerms: boolean;
}

export default function SignUpWithPlanForm({
  planCode,
  billingInterval,
  onClose,
}: SignUpWithPlanFormProps) {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    companyName: "",
    agreeToTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if there's a referral code
  const referralCode = sessionStorage.getItem("referral_code");
  const hasReferral = !!referralCode;

  const planNames: Record<string, string> = {
    fma_basic: "FMA Basic (Free)",
    fma_pro: "FMA Pro",
    fma_elite: "FMA Elite",
    ams_standard: "AMS Standard",
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      setIsLoading(false);
      return;
    }

    if (!formData.agreeToTerms) {
      setError("You must agree to the Terms & Conditions");
      setIsLoading(false);
      return;
    }

    try {
      console.log("[SignUpForm] Calling signup Edge Function...");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Check for referral code in sessionStorage
      const referralCode = sessionStorage.getItem("referral_code");
      if (referralCode) {
        console.log("[SignUpForm] Using referral code:", referralCode);
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/auth_signup-with-plan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            first_name: formData.firstName,
            last_name: formData.lastName,
            company_name: formData.companyName,
            selected_plan_code: planCode,
            billing_interval: billingInterval,
            referral_code: referralCode || undefined, // Include if available
          }),
        }
      );

      const data = await response.json();

      console.log("[SignUpForm] Response:", data);

      if (!response.ok || !data.ok) {
        throw new Error(data.error || data.message || "Signup failed");
      }

      const result = data.data;

      if (!result.success) {
        throw new Error(result.error || "Signup failed");
      }

      // CRITICAL: Establish session BEFORE redirecting anywhere
      if (result.access_token && result.refresh_token) {
        console.log("[SignUpForm] Establishing session with tokens...");
        
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.access_token,
          refresh_token: result.refresh_token
        });
        
        if (sessionError) {
          console.error("[SignUpForm] Failed to set session:", sessionError);
          throw new Error("Failed to establish session");
        }
        
        console.log("[SignUpForm] Session established successfully!");
      }

      // Handle success - now user is logged in
      if (result.requires_payment) {
        console.log("[SignUpForm] Redirecting to Stripe checkout...");
        // Redirect to Stripe Checkout
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          throw new Error("No checkout URL returned");
        }
      } else {
        console.log("[SignUpForm] Free plan - redirecting to welcome page...");
        // Free plan - redirect to FMA welcome page
        window.location.href = "/fma-welcome";
      }
    } catch (err) {
      console.error("[SignUpForm] Error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again."
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-9999 p-4 overflow-y-auto">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl relative my-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 rounded-full hover:bg-white/10 text-white/80 hover:text-white p-2 transition-colors duration-200 z-10"
          disabled={isLoading}
        >
          <X className="h-4 w-4" />
        </button>

        <CardHeader className="pt-8">
          <CardTitle className="text-2xl font-semibold text-white mb-2">
            Create Your Account
          </CardTitle>
          <CardDescription className="text-gray-300">
            Sign up for{" "}
            <span className="font-semibold text-white">
              {planNames[planCode]}
            </span>
            {hasReferral && planCode === "fma_elite" && (
              <span className="ml-2 inline-flex items-center gap-1 text-green-600 font-semibold">
                🎁 +1,000 bonus points!
              </span>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="firstName"
                  className="text-white/90 font-medium"
                >
                  First Name *
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-white/90 font-medium">
                  Last Name *
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="companyName"
                className="text-white/90 font-medium"
              >
                Company Name *
              </Label>
              <Input
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your Agency Name"
                required
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/90 font-medium">
                Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@agency.com"
                required
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/90 font-medium">
                Password *
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="At least 8 characters"
                required
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="text-white/90 font-medium"
              >
                Confirm Password *
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter password"
                required
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/15 focus:border-orange-400/50 rounded-xl h-12 transition-all duration-200"
              />
            </div>

            <div className="flex items-start space-x-3 pt-2">
              <input
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                disabled={isLoading}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10 text-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
              />
              <label
                htmlFor="agreeToTerms"
                className="text-sm text-gray-300 cursor-pointer"
              >
                I agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300 underline"
                >
                  Terms & Conditions
                </a>
              </label>
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium rounded-xl shadow-lg shadow-orange-500/25 transition-all duration-200 disabled:opacity-50 disabled:shadow-none border-0"
                disabled={isLoading || !formData.agreeToTerms}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  "Let's Go"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
